import type { Melody, MelodyTake } from '@tunasong/schemas'

/**
 * Takes: versions of a melody's notes, kept to compare (melody toolkit plan M5; spec §5.5; plan D2, D3).
 *
 * `notes` stays the current notes, so every reader of a melody (trainers, phrases, staff, tab) is untouched.
 * `currentTakeId` names the take `notes` is. Before switching, the current notes are written into the
 * current take, or kept as a take of their own when they are none yet: switching never loses work. Each
 * function returns a whole melody, one edit, so one undo step. Ids come from the caller.
 */

const namePattern = /^Take (\d+)$/

/** "Take n", one past the highest number used, so a name never repeats after a rename */
const nextTakeName = (takes: MelodyTake[]) => {
  const used = takes.flatMap(take => {
    const match = namePattern.exec(take.name)
    return match ? [Number(match[1])] : []
  })
  return `Take ${Math.max(takes.length, ...used) + 1}`
}

/** The takes with the current notes written into the current take, or kept as a new take `keptId` */
const keepCurrent = (melody: Melody, keptId: string): MelodyTake[] => {
  const takes = melody.takes ?? []
  const current = takes.find(take => take.id === melody.currentTakeId)
  if (current) {
    return takes.map(take => (take.id === current.id ? { ...take, notes: melody.notes } : take))
  }
  if (melody.notes.length === 0) {
    return takes
  }
  return [...takes, { id: keptId, name: nextTakeName(takes), notes: melody.notes }]
}

/** `Save as take`: the current notes as a new take, made current; the take they were stays as it was */
export const saveAsTake = (melody: Melody, id: string): Melody => {
  const takes = melody.takes ?? []
  return {
    ...melody,
    takes: [...takes, { id, name: nextTakeName(takes), notes: melody.notes }],
    currentTakeId: id,
  }
}

/** A blank name is not a name, so the take keeps the one it had */
export const renameTake = (melody: Melody, id: string, name: string): Melody => {
  const trimmed = name.trim()
  if (trimmed === '') {
    return melody
  }
  return { ...melody, takes: (melody.takes ?? []).map(take => (take.id === id ? { ...take, name: trimmed } : take)) }
}

/**
 * `Use this take`: its notes become `notes` and it becomes current, together. The notes there were are
 * kept first, in their take or as a new one `keptId`. A take that is not there changes nothing.
 */
export const switchToTake = (melody: Melody, id: string, keptId: string): Melody => {
  const target = (melody.takes ?? []).find(take => take.id === id)
  if (!target) {
    return melody
  }
  const takes = keepCurrent(melody, keptId)
  return { ...melody, takes, notes: target.notes, currentTakeId: id }
}

/**
 * Plan D2: a capture lands as notes, as a new take, and nothing is lost. The notes there were are kept
 * (`ids.kept` when they are no take yet); the captured melody, with its tempo and source, becomes current.
 */
export const addCaptureTake = (melody: Melody, captured: Melody, ids: { kept: string; capture: string }): Melody => {
  const takes = keepCurrent(melody, ids.kept)
  return {
    ...captured,
    takes: [...takes, { id: ids.capture, name: nextTakeName(takes), notes: captured.notes }],
    currentTakeId: ids.capture,
  }
}
